import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { indentWithTab, redo } from '@codemirror/commands';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { xml } from '@codemirror/lang-xml';
import { Compartment, EditorState, Extension } from '@codemirror/state';
import { EditorView, keymap, ViewUpdate } from '@codemirror/view';
import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { basicSetup } from 'codemirror';

@Component({
  selector: 'mc-cm-editor',
  templateUrl: './cm-editor.component.html',
  styleUrls: ['./cm-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmEditorComponent implements OnInit, OnChanges {
  @Input() content: string;
  @Input() lang: CodeLang;
  @Input() readonly?: boolean = false;
  @Input() wrapLines?: boolean = false;

  @Output() contentChange: EventEmitter<ViewUpdate> = new EventEmitter<ViewUpdate>();
  @Output() editorStateChange: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectionChange: EventEmitter<void> = new EventEmitter<void>();
  @Output() wrapLinesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  get editorState(): EditorState {
    return this.editorView?.state;
  }

  constructor(private elementRef: ElementRef, private ngZone: NgZone) { }

  editorView: EditorView;
  private readonlyCompartment: Compartment = new Compartment();
  private wrapLinesCompartment: Compartment = new Compartment();
  private langCompartment: Compartment = new Compartment();

  ngOnInit() {
    this.editorView = new EditorView({
      parent: this.elementRef.nativeElement,
      state: this.createEditorState()
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.content) {
      if (this.editorView) {
        this.editorView.scrollDOM.scroll(0, 0);
        this.editorView.setState(this.createEditorState());
      }
    }

    if (changes.lang) {
      if (this.editorView) {
        this.editorView.dispatch({
          effects: this.langCompartment.reconfigure(this.getLangExtension(this.lang))
        });
      }
    }

    if (changes.readonly) {
      if (this.editorView) {
        this.editorView.dispatch({
          effects: this.readonlyCompartment.reconfigure(EditorState.readOnly.of(this.readonly))
        });
      }
    }

    if (changes.wrapLines) {
      if (this.editorView) {
        this.editorView.dispatch({
          effects: this.wrapLinesCompartment.reconfigure(this.wrapLines ? EditorView.lineWrapping : [])
        });
      }

      this.wrapLinesChange.emit(this.wrapLines);
    }
  }

  public reset() {
    if (this.editorView) {
      this.editorView.scrollDOM.scroll(0, 0);
      this.editorView.setState(this.createEditorState());
    }
  }

  private createEditorState(): EditorState {
    return EditorState.create({
      doc: this.content ?? '',
      extensions: [
        basicSetup,
        keymap.of([
          { key: 'Mod-y', run: redo, preventDefault: true }, // For Cmd+Y redo support on Macs
          indentWithTab
        ]),
        this.readonlyCompartment.of(EditorState.readOnly.of(this.readonly)),
        this.wrapLinesCompartment.of(this.wrapLines ? EditorView.lineWrapping : []),
        this.langCompartment.of(this.getLangExtension(this.lang)),
        EditorView.updateListener.of(viewUpdate => {
          this.ngZone.run(() => {
            if (viewUpdate.docChanged) {
              this.contentChange.emit(viewUpdate);
            }

            if (viewUpdate.selectionSet) {
              this.selectionChange.emit();
            }

            this.editorStateChange.emit();
          });
        })
      ]
    });
  }

  private getLangExtension(lang: CodeLang): Extension {
    switch (lang) {
      case 'css':
        return css();
      case 'html':
        return html();
      case 'javascript':
        return javascript();
      case 'json':
        return json();
      case 'xml':
      case 'flare':
        return xml();
      default:
        return [];
    }
  }
}
